/**
 * Export library — the single entry point for every report or
 * DataTable export across the Console.
 *
 * Per M3X.REPORTS §0.2 export-as-is lock:
 *   - Every export contains EXACTLY: visible columns + active
 *     filters + full filtered set (NOT current page) + group/sort
 *     state + same calculated values + same locale formatting +
 *     brand header.
 *
 * Use `exportReport()` from anywhere — DataTable, ReportPage,
 * Custom Builder. Pass an `ExportPayload` built from the
 * currently-visible view state.
 */

import { exportCsv as _exportCsv } from "./csv";
import { buildExportFilename } from "./filename";
import { exportPdf as _exportPdf } from "./pdf";
import type { ExportFormat, ExportPayload } from "./types";
import { exportXlsx as _exportXlsx } from "./xlsx";

export type { ExportColumn, ExportFormat, ExportPayload } from "./types";
export { buildExportFilename };
export { buildCsvString } from "./csv";

/** The single entry point. */
export async function exportReport(
  payload: ExportPayload,
  format: ExportFormat,
): Promise<void> {
  const filename = buildExportFilename({
    title: payload.title,
    period: payload.period ?? null,
    ext: format,
    now: payload.generatedAt,
  });
  switch (format) {
    case "xlsx":
      return _exportXlsx(payload, filename);
    case "csv":
      return _exportCsv(payload, filename);
    case "pdf":
      return _exportPdf(payload, filename);
  }
}

// ---------------------------------------------------------------------------
// LEGACY shims — preserved so existing callers compile until they migrate
// to the new `exportReport(payload, format)` shape. These delegate to the
// new exporters with a minimal payload (no period, no generatedBy, no
// active filters — brand header still renders).
// ---------------------------------------------------------------------------

const _now = (): Date => new Date();

/** @deprecated — pass an ExportPayload via exportReport() instead. */
export async function exportToXlsx(
  rows: Array<Record<string, unknown>>,
  filename: string,
  _sheetName?: string,
): Promise<void> {
  const columns = inferColumns(rows);
  const payload: ExportPayload = {
    title: deriveTitleFromFilename(filename),
    columns,
    rows,
    generatedAt: _now(),
  };
  return _exportXlsx(payload, filename);
}

/** @deprecated — pass an ExportPayload via exportReport() instead. */
export async function exportToCsv(
  rows: Array<Record<string, unknown>>,
  filename: string,
): Promise<void> {
  const columns = inferColumns(rows);
  const payload: ExportPayload = {
    title: deriveTitleFromFilename(filename),
    columns,
    rows,
    generatedAt: _now(),
  };
  return _exportCsv(payload, filename);
}

function inferColumns(rows: Array<Record<string, unknown>>): ExportPayload["columns"] {
  if (rows.length === 0) return [];
  return Object.keys(rows[0]).map((key) => ({
    key,
    label: key,
    type: inferType(rows[0][key]),
  }));
}

function inferType(value: unknown): ExportPayload["columns"][number]["type"] {
  if (value instanceof Date) return "date";
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  return "string";
}

function deriveTitleFromFilename(filename: string): string {
  const stem = filename.replace(/\.(xlsx|csv|pdf)$/i, "");
  return stem.replace(/_/g, " ").trim() || "Report";
}
